import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import type { Udalost } from '../api/JsdhNotifikaceService/generated/models/Udalost';
import type { Odpoved } from '../api/JsdhNotifikaceService/generated/models/Odpoved';
import type { Technika } from '../api/JsdhNotifikaceService/generated/models/Technika';
import type { PoplachTechnikaResponse } from '../api/JsdhNotifikaceService/generated/models/PoplachTechnika';
import type { PosilovaNotifikaceLogResponse } from '../api/JsdhNotifikaceService/generated/models/PosilovaNotifikaceLog';
import type { UdalostRouteResponse } from '../api/JsdhNotifikaceService/generated/models/UdalostRoute';
import type { JednotkaDto } from '../api/JsdhNotifikaceService/generated/models/JednotkaDto';
import type { PoplachConfig } from '../api/JsdhNotifikaceService/generated/models/DeviceToken';
import { Vyjezd2026InfoPanel } from './Vyjezd2026InfoPanel';
import { Vyjezd2026MapaSection } from './Vyjezd2026MapaSection';
import { Vyjezd2026TechnikaSection, type TechnikaCard } from './Vyjezd2026TechnikaSection';
import { Vyjezd2026OdpovediSection } from './Vyjezd2026OdpovediSection';
import { useNocniRezim } from '../hooks/useNocniRezim';
import { extractVolaciZnak } from '../utils/technikaParser';
import { useThemeMode } from '../theme/ThemeContext';
import { createAppTheme } from '../theme/createAppTheme';
import { getColorScheme } from '../theme/colorSchemes';

interface Vyjezd2026WrapperProps {
    aktivniPoplach: Udalost;
    odpovedi: Odpoved[];
    allTechnika: Technika[];
    technikaSources: PoplachTechnikaResponse[];
    notifikaceLog: PosilovaNotifikaceLogResponse[];
    routeData: UdalostRouteResponse | null;
    jednotka?: JednotkaDto;
    poplachConfig: PoplachConfig;
    deviceToken?: string;
    isPreview?: boolean;
}

export const Vyjezd2026Wrapper: React.FC<Vyjezd2026WrapperProps> = ({
    aktivniPoplach, odpovedi, allTechnika, technikaSources, notifikaceLog,
    routeData, jednotka, poplachConfig, deviceToken, isPreview,
}) => {
    // Sestavit uspořádaný seznam karet techniky v pořadí, jak přišly v emailu (technikaJednotky).
    // Každý text se pokusí spárovat se zdrojem v poplach_technika nejprve přes textPuvodni
    // (BE plní) a fallback přes volací znak (mock kompatibilita + robustnost při chybějícím textPuvodni).
    // Pokud match selže, karta se renderuje jako 'unmatched' fallback s daty extrahovanými z textu.
    const technikaCards = useMemo<TechnikaCard[]>(() => {
        const sourceByText = new Map<string, PoplachTechnikaResponse>();
        const sourceByVolaci = new Map<string, PoplachTechnikaResponse>();
        technikaSources.forEach(s => {
            if (s.textPuvodni) sourceByText.set(s.textPuvodni.trim(), s);
            if (s.volaciZnak) sourceByVolaci.set(s.volaciZnak.trim(), s);
        });
        return (aktivniPoplach.technikaJednotky ?? [])
            .map(t => t?.trim())
            .filter((t): t is string => !!t)
            .map((text): TechnikaCard => {
                const byText = sourceByText.get(text);
                if (byText) {
                    const tech = allTechnika.find(t => t.id === byText.technikaId);
                    if (tech) return { type: 'matched', technika: tech, source: byText };
                }
                const byVolaci = sourceByVolaci.get(extractVolaciZnak(text));
                if (byVolaci) {
                    const tech = allTechnika.find(t => t.id === byVolaci.technikaId);
                    if (tech) return { type: 'matched', technika: tech, source: byVolaci };
                }
                return { type: 'unmatched', text };
            });
    }, [aktivniPoplach.technikaJednotky, technikaSources, allTechnika]);

    const stationGps = useMemo(() => {
        if (jednotka?.adresaLat && jednotka?.adresaLng) {
            return `${jednotka.adresaLat},${jednotka.adresaLng}`;
        }
        return undefined;
    }, [jednotka?.adresaLat, jednotka?.adresaLng]);

    const casVyjezdMinuty = jednotka?.casVyjezdMinuty || 10;

    // Noční režim: vyřeší se z poplachConfig.nocniRezim ('auto' default → dle hodiny;
    // 'dark' → vždy noční; 'light' → vždy denní). Lokální MUI theme s odpovídajícím
    // palette.mode přepíše globální theme jen pro tuto kořenovou sekci.
    const isDark = useNocniRezim(poplachConfig.nocniRezim);
    const { colorScheme } = useThemeMode();
    const localTheme = useMemo(() => {
        const base = createAppTheme(getColorScheme(colorScheme), isDark);
        if (!isDark) return base;
        // V noční režimu zesvětlujeme sekundární text — defaultní #b0b0b0 z createAppTheme
        // je proti tmavému pozadí málo kontrastní (typický scénář: posily, "Objekt: ..."
        // labely, upřesnění); také text.disabled bumpneme aby "+ X dalších" bylo čitelné.
        return createTheme(base, {
            palette: {
                text: {
                    primary: '#ffffff',
                    secondary: '#dcdcdc',
                    disabled: '#a8a8a8',
                },
            },
        });
    }, [colorScheme, isDark]);

    return (
        <MuiThemeProvider theme={localTheme}>
        <Box sx={{
            height: '100vh', overflow: 'hidden',
            display: 'flex', flexDirection: 'row',
            position: 'relative',
            bgcolor: 'background.default',
        }}>
            {isPreview && (
                <Box sx={{
                    position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1100,
                    bgcolor: 'warning.main', color: 'warning.contrastText',
                    py: 0.5, px: 2, textAlign: 'center',
                }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                        VZOROVÝ POPLACH — testovací zobrazení
                    </Typography>
                </Box>
            )}

            {/* Levý sloupec: InfoPanel (60% výšky, fixní) + Odpovědi (40% výšky, fixní).
                Uvnitř InfoPanel: detaily nahoře, posily+zprávy kotveny ke spodnímu okraji panelu (mt:auto)
                → mezera vznikne za "Upřesnění", posily/zprávy těsně nad odpověďmi. */}
            <Box sx={{
                width: 'var(--dashboard-vyjezd-left-width, 35%)',
                height: '100%',
                display: 'flex', flexDirection: 'column',
                minWidth: 0,
                borderRight: '1px solid',
                borderColor: 'divider',
            }}>
                <Box sx={{
                    flex: '0 0 60%',
                    minHeight: 0,
                    display: 'flex',
                    overflow: 'hidden',
                }}>
                    <Vyjezd2026InfoPanel aktivniPoplach={aktivniPoplach} notifikace={notifikaceLog} />
                </Box>
                <Box sx={{
                    flex: '0 0 40%',
                    minHeight: 0,
                    display: 'flex',
                }}>
                    <Vyjezd2026OdpovediSection
                        odpovedi={odpovedi}
                        casVyhlaseni={aktivniPoplach.casVyhlaseni}
                        pravidla={poplachConfig.pravidlaVyjezdu}
                        showSpecializace={poplachConfig.zobrazitSpecializace !== false}
                        showFunkce={poplachConfig.zobrazitFunkce !== false}
                    />
                </Box>
            </Box>

            {/* Pravý sloupec: Mapa (70%) + Technika (30%) */}
            <Box sx={{
                flex: 1,
                height: '100%',
                display: 'flex', flexDirection: 'column',
                minWidth: 0,
            }}>
                <Box sx={{
                    height: 'var(--dashboard-vyjezd-mapa-height, 70%)',
                    minHeight: 0,
                    display: 'flex',
                }}>
                    <Vyjezd2026MapaSection
                        aktivniPoplach={aktivniPoplach}
                        mapConfig={poplachConfig.mapa}
                        mapaLayout={poplachConfig.mapaLayout || 'misto'}
                        routeData={routeData}
                        stationGps={stationGps}
                        casVyjezdMinuty={casVyjezdMinuty}
                    />
                </Box>
                <Box sx={{
                    height: 'var(--dashboard-vyjezd-technika-height, 30%)',
                    minHeight: 0,
                    display: 'flex',
                    borderTop: '1px solid',
                    borderColor: 'divider',
                }}>
                    <Vyjezd2026TechnikaSection
                        cards={technikaCards}
                        deviceToken={deviceToken}
                    />
                </Box>
            </Box>
        </Box>
        </MuiThemeProvider>
    );
};
